import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {AddressModel} from '@service/models';
import {ContentService} from '../../../constants/content.service';
import {EditAddressComponent} from './edit-address/edit-address.component';
import {AppConfigService} from '@service/config/app-config.service';

@Component({
    selector: 'creditsnap-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => AddressComponent),
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => AddressComponent),
        multi: true
    }]
})

export class AddressComponent implements AfterViewInit, ControlValueAccessor, Validator, OnChanges {
    @ViewChild('fullAddress', {read: NgControl}) fullAddressEle: NgControl;
    @ViewChild('fullAddress', {read: ElementRef}) fullAddressInputRef: ElementRef<HTMLInputElement>;
    @ViewChild('hintEditLink') hintEditLinkRef: ElementRef<HTMLElement>;
    @Input('data-test-id') testId: string = 'address';
    @Input() id = 'fullAddress';
    @Input() name = 'fullAddress';
    @Input() submitted = false;
    @Input() isMailingAddress = false;
    @Input() readonly = false;
    @Input() readonlyAddressField = false;
    @Input() disabled: any = {
        streetAddress: false,
        zipCode: false,
        city: false,
        state: false,
        county: false,
        country: false,
    };
    @Output() valueChange: EventEmitter<AddressModel> = new EventEmitter<AddressModel>();
    appConfig: any = AppConfigService.config;
    /**
     * Holds the current value of the slider
     */
    value: AddressModel = new AddressModel();
    isAddressSelected = false;

    constructor(
        private zone: NgZone,
        public contentService: ContentService,
        private cdr: ChangeDetectorRef,
        private _matDialog: MatDialog,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.submitted && changes.submitted.currentValue && this.fullAddressEle) {
            this.fullAddressEle.control.markAsTouched();
        }

        if (changes.isMailingAddress) {
            this.isMailingAddress = !!changes.isMailingAddress.currentValue;
        }
    }

    ngAfterViewInit(): void {
        this.addCustomValidatorToElement();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.addressValidator(control);
    }

    onAddressKeydown(event: KeyboardEvent) {
        if (this.readonlyAddressField)
            return;
        if (this.isAddressSelected && (event.key === 'Delete' || event.key === 'Backspace')) {
            this.clearAddress();
        }
    }

    onLinkKeydown(event: KeyboardEvent) {
        if (this.readonlyAddressField)
            return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.editAddress();
        }
    }

    touch() {
        this.fullAddressEle.control.markAsTouched();
    }

    /**
     * Invoked when the model has been changed
     */
    onChange(_: AddressModel) {
    }

    /**
     * Invoked when the model has been touched
     */
    onTouched: () => void = () => {
    }

    /**
     * Method that is invoked on an update of a model.
     */
    updateChanges() {
        this.onChange(this.value);
    }

    ///////////////
    // OVERRIDES //
    ///////////////

    /**
     * Writes a new item to the element (model -> view, called by Angular forms).
     *
     * Deliberately does NOT emit `valueChange`. Emitting here echoes every programmatic write
     * back to the parent; parents that patch the bound control in response (e.g.
     * patchAddressFormValue in add-personal-information) trigger writeValue again and the two
     * recurse until "Maximum call stack size exceeded". That overflow also corrupted Angular's
     * cached view for the clear-icon *ngIf, which then failed on every later render with
     * "Cannot read properties of null (reading 'flags')".
     *
     * It must not call `onChange` either. When the parent swaps its FormGroup (createForm() runs
     * more than once), Angular registers a throwing noop as onChange and then calls writeValue
     * before registering the real one. Calling onChange here throws "There is no FormControl
     * instance attached to form control element with name: 'address'", aborting the rebind and
     * leaving every control after address (unit, email, ...) wired to the old FormGroup, so the
     * Continue button is computed from controls the user is no longer editing.
     * @param value the value
     */
    writeValue(value: AddressModel): void {
        this.applyValue(value, false);
    }

    /**
     * Applies a user-driven change (autocomplete selection, edit dialog, clear) and notifies the
     * parent through `valueChange`.
     */
    private setUserValue(value: AddressModel): void {
        this.applyValue(value, true);
    }

    private applyValue(value: AddressModel, isUserChange: boolean): void {
        this.value = value || new AddressModel();
        const {fullAddress, streetAddress, city, state, country, zipCode} = this.value;
        if (!fullAddress) {
            this.value.fullAddress = (streetAddress && country ? `${streetAddress}, ${city}, ${state}, ${zipCode}, ${country}` : '').toUpperCase();
        }
        this.isAddressSelected = !!(this.value.fullAddress || '').trim();
        if (isUserChange) {
            this.valueChange.emit(this.value);
            this.updateChanges();
        }
    }

    /**
     * Registers a callback function that should be called when the control's value changes in the UI.
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback function that should be called when the control receives a blur event.
     * @param fn
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    addressValidator(control: AbstractControl): ValidationErrors | null {
        let error = null;
        if (!this.value || !this.value?.fullAddress) {
            this.value.streetAddress = this.value.state = this.value.city = this.value.country = this.value.zipCode = '';
            error = {'required': true};
        } else if (!this.value.streetAddress ||
            !this.value.state ||
            !this.value.city ||
            !this.value.country ||
            !this.value.zipCode) {
            error = {'invalid': true};
        }

        if (this.fullAddressEle) {
            this.fullAddressEle.control.setErrors(error);
        }

        return error;
    }


    addCustomValidatorToElement() {
        this.fullAddressEle.control.addValidators((control: AbstractControl): ValidationErrors | null => this.addressValidator(control));
    }


    addressSelected(addrObj) {
        this.zone.run(() => {
            this.value.streetAddress = ((addrObj.street_number || '') + ' ' + (addrObj.route || '')).trim();
            this.value.fullAddress = addrObj.formatted_address || this.value.streetAddress;
            this.value.zipCode = addrObj.postal_code || '';
            this.value.city = addrObj.locality || '';
            this.value.state = addrObj.admin_area_l1 || '';
            this.value.county = addrObj.county || '';
            this.value.country = (addrObj.country3 || '').toUpperCase();
        });
        this.setUserValue(this.value);
        this.fullAddressEle.control.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    editAddress() {
    const editAddressDialog = this._matDialog.open(EditAddressComponent, {
        data: {
            address: {...this.value},
            isMailingAddress: this.isMailingAddress,addressTypeName: this.name
        },
        width: '600px',
        maxWidth: '90vw',
        disableClose: true,
        restoreFocus: false,
        ariaLabelledBy: 'edit-address-title',
    });

    editAddressDialog.afterOpened().subscribe(() => {
        requestAnimationFrame(() => {
            const titleEl = document.getElementById('edit-address-title');
            titleEl?.focus();
        });
    });

    // Move focus to the address input while the dialog is still in the DOM so the
    // CDK focus trap cannot recapture it and _restoreFocus() finds activeElement
    // already outside the dialog and leaves it alone.
    editAddressDialog.beforeClosed().subscribe(() => {
        this.fullAddressInputRef?.nativeElement?.focus();
    });

    editAddressDialog.afterClosed().subscribe((res) => {
        if (res) {
            this.setUserValue(res);
            this.fullAddressEle.control.updateValueAndValidity();
            this.cdr.detectChanges();
        }
    });
}

    clearAddress() {
        //if editing the address we need to keep addressId so that's why we merged the object.
        if (this.readonlyAddressField)
            return;
        this.setUserValue(this.value.addressId ? {...this.value, ...new AddressModel()} : new AddressModel());
    }

}
